import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  WaveCloudAPI,
  WaveGraphQLError,
  WaveRole,
  isPresenceFresh,
  isYjsWaveFormat,
  buildWavePresenceDiagnostics,
  mergePolledPresences,
  mergePresenceRecord,
  notifyWaveCommentMentionPush,
  notifyWaveInvitePush,
  parseCommentMentions,
  participantRecordsForTodoMentions,
  type WavePresenceRecord,
  type WaveLastSeenRecord,
  type WaveRecord,
  type WaveStatus,
} from "@vida/wave-core";
import { WaveEditor } from "../editor/WaveEditor";
import { WaveChatShell } from "../chat/WaveChatShell";
import { useWaveChat } from "../../hooks/useWaveChat";
import {
  usePlaybackSource,
  useWaveBridge,
  useWaveRealtimeExtras,
} from "../../hooks/useWave";
import {
  createWaveApiConfig,
  getRealtimeStatus,
  subscribeRealtimeStatus,
  trackActivity,
} from "../../services/waveSession";
import { waveSessionClientId } from "../../services/waveClientId";
import { showWaveNotification } from "../../services/notifications";
import { EMPTY_BLIPS } from "../../utils/stableEmpty";
import { WaveShareDialog } from "./WaveShareDialog";
import { WaveEditorPlaceholder, WaveSyncProgressBar } from "./WaveLoadingOverlay";
import { WaveSyncDiagnosticsBar } from "./WaveSyncDiagnosticsBar";
import { WaveSyncStatusStrip } from "./WaveSyncStatusStrip";
import {
  getWaveSyncDebugVisible,
  subscribeWaveSyncDebugVisible,
} from "../../services/waveSyncDebug";
import { getPublicBaseUrl } from "../../config";
import { currentUserDisplayName, currentUserEmail, currentUserSub } from "../../auth";
import { appendWaveSyncLog } from "../../services/waveSyncLog";
import { maybeRunCommentAgentReply } from "../../services/waveCommentAgent";
import { useWaveCommentMentionNotifications } from "../../hooks/useWaveCommentMentionNotifications";

type Props = {
  wave: WaveRecord;
  onBack?: () => void;
  backLabel?: string;
  sidebarCollapsed?: boolean;
  onReopenSidebar?: () => void;
  onWaveReplace: (wave: WaveRecord) => void;
  onWaveStatusEvent: (incoming: WaveRecord) => void;
  onInviteSuccess?: () => void;
  onPresenceError?: (message: string | null) => void;
  onArchiveToggle?: () => void;
  onRenameTitle?: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
};

const MAX_PRESENCE_WATCHDOG_RETRIES = 3;

export function WaveWorkspace({
  wave,
  onBack,
  backLabel,
  sidebarCollapsed = false,
  onReopenSidebar,
  onWaveReplace,
  onWaveStatusEvent,
  onInviteSuccess,
  onPresenceError,
  onArchiveToggle,
  onRenameTitle,
  isFavorite = false,
  onToggleFavorite,
}: Props) {
  const waveId = wave.waveId;
  const isYjsWave = isYjsWaveFormat(wave.formatVersion);
  const [legacyBlips, setLegacyBlips] = useState(EMPTY_BLIPS);
  const [presences, setPresences] = useState<WavePresenceRecord[]>([]);
  const [lastSeen, setLastSeen] = useState<WaveLastSeenRecord[]>([]);
  const [playbackEnabled, setPlaybackEnabled] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [presenceRetryKey, setPresenceRetryKey] = useState(0);
  const [presencePollingOnly, setPresencePollingOnly] = useState(false);
  const [presenceLastError, setPresenceLastError] = useState<string | null>(null);
  const lastPresenceEventAtRef = useRef<number>(Date.now());
  const presenceWatchdogRetriesRef = useRef(0);

  const {
    bridge,
    emitOps,
    flushDocumentSync,
    syncEngine,
    endpointReady,
    editor,
    documentRevision,
    documentReady,
    networkHydrating,
    usesYjs,
    syncDiagnostics,
  } = useWaveBridge(waveId, wave);
  const chat = useWaveChat({
    waveId,
    waveTitle: wave.title,
    syncEngine,
    currentUserSub: currentUserSub(),
    participants: wave.participants,
    open: chatOpen,
  });
  const { baseline, ops, refresh: refreshPlayback } = usePlaybackSource(
    wave,
    playbackEnabled,
  );

  const publishPresence = useCallback(
    async (
      anchor: string | null,
      cursorOffset: number | null = null,
      cursorEndOffset: number | null = null,
    ) => {
      try {
        await WaveCloudAPI.publishPresence(
          createWaveApiConfig(),
          waveId,
          waveSessionClientId(),
          anchor,
          cursorOffset,
          cursorEndOffset,
        );
        onPresenceError?.(null);
        setPresenceLastError(null);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Presenza non pubblicata";
        if (e instanceof WaveGraphQLError && e.isAuthOrPermissionFailure) {
          onPresenceError?.(message);
          trackActivity("Presenza", message);
          setPresenceLastError(message);
        } else {
          setPresenceLastError(message);
          appendWaveSyncLog("presence", "publishPresence failed", message);
        }
      }
    },
    [waveId, onPresenceError],
  );

  useEffect(() => {
    if (isYjsWave) {
      setLegacyBlips(EMPTY_BLIPS);
      return;
    }
    let cancelled = false;
    const cfg = createWaveApiConfig();
    void WaveCloudAPI.listBlips(cfg, waveId)
      .then((items) => {
        if (!cancelled) setLegacyBlips(items);
      })
      .catch(() => {
        /* legacy blips optional */
      });
    return () => {
      cancelled = true;
    };
  }, [waveId, isYjsWave]);

  useEffect(() => {
    void publishPresence(null, null, null);
  }, [publishPresence]);

  useEffect(() => {
    let cancelled = false;
    const cfg = createWaveApiConfig();
    const refresh = async () => {
      try {
        const items = await WaveCloudAPI.listPresences(cfg, waveId);
        if (cancelled) return;
        const fresh = items.filter(isPresenceFresh);
        if (fresh.length > 0) {
          lastPresenceEventAtRef.current = Date.now();
          presenceWatchdogRetriesRef.current = 0;
        }
        setPresences((prev) => mergePolledPresences(prev, fresh));
        setPresenceLastError(null);
      } catch (e) {
        const message = e instanceof Error ? e.message : "listWavePresences failed";
        setPresenceLastError(message);
        appendWaveSyncLog("presence", "listWavePresences failed", message);
      }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [waveId]);

  useEffect(() => {
    let cancelled = false;
    const cfg = createWaveApiConfig();
    const refresh = async () => {
      try {
        const items = await WaveCloudAPI.listLastSeen(cfg, waveId);
        if (!cancelled) setLastSeen(items);
      } catch (e) {
        const message = e instanceof Error ? e.message : "listWaveLastSeen failed";
        setPresenceLastError(message);
        appendWaveSyncLog("presence", "listWaveLastSeen failed", message);
      }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [waveId]);

  const onStatus = useCallback(
    (incoming: WaveRecord) => {
      onWaveStatusEvent(incoming);
      showWaveNotification("Wave aggiornata", `Stato: ${incoming.status}`);
    },
    [onWaveStatusEvent],
  );

  const onPresence = useCallback((p: WavePresenceRecord) => {
    lastPresenceEventAtRef.current = Date.now();
    presenceWatchdogRetriesRef.current = 0;
    setPresences((prev) => mergePresenceRecord(prev, p));
  }, []);

  useWaveRealtimeExtras(
    waveId,
    syncEngine,
    onStatus,
    onPresence,
    presenceRetryKey,
    !presencePollingOnly,
  );

  const userSub = currentUserSub();
  const mentionRoster = useMemo(
    () =>
      participantRecordsForTodoMentions({
        participants: wave.participants,
        presences,
        lastSeen,
        currentSub: userSub,
        ownerSub: wave.ownerSub,
        currentUserEmail: currentUserEmail(),
      }),
    [wave.participants, wave.ownerSub, presences, lastSeen, userSub],
  );

  const liveComments = useMemo(
    () => editor?.comments ?? [],
    [editor, documentRevision],
  );

  useWaveCommentMentionNotifications({
    waveTitle: wave.title,
    comments: liveComments,
    mentionRoster,
    currentUserSub: userSub,
    enabled: usesYjs && Boolean(editor),
  });

  const resolveAuthorName = useCallback(() => {
    const sub = currentUserSub();
    const participant = wave.participants.find((p) => p.sub === sub);
    const email = participant?.email?.trim() || currentUserEmail().trim();
    if (email) return email;
    return (
      participant?.displayName?.trim() ||
      currentUserDisplayName()
    );
  }, [wave.participants]);

  const realtimeStatus = useSyncExternalStore(
    subscribeRealtimeStatus,
    getRealtimeStatus,
    () => "offline" as const,
  );

  const realtimeConnected = realtimeStatus === "online";

  const presenceDiagnostics = buildWavePresenceDiagnostics({
    participants: wave.participants,
    presences,
    lastSeen,
    currentSub: currentUserSub(),
    ownerSub: wave.ownerSub,
    realtimeConnected,
    presencePollingOnly,
    lastError: presenceLastError,
  });

  const showSyncDebugBar = useSyncExternalStore(
    subscribeWaveSyncDebugVisible,
    getWaveSyncDebugVisible,
    () => false,
  );

  const engineBooting = !syncEngine;
  const previewBlocks = editor?.blocks ?? bridge.blocks;
  const hasPreviewContent = previewBlocks.length > 0;
  const showPlaceholder =
    !hasPreviewContent && (engineBooting || !documentReady);
  const showSyncBar =
    hasPreviewContent &&
    !engineBooting &&
    (networkHydrating || !documentReady);

  useEffect(() => {
    if (!syncEngine || presencePollingOnly) return;
    const watchdog = setTimeout(() => {
      const stale = Date.now() - lastPresenceEventAtRef.current > 5000;
      if (!stale) return;
      if (presenceWatchdogRetriesRef.current >= MAX_PRESENCE_WATCHDOG_RETRIES) {
        setPresencePollingOnly(true);
        return;
      }
      presenceWatchdogRetriesRef.current += 1;
      setPresenceRetryKey((k) => k + 1);
    }, 5000);
    return () => clearTimeout(watchdog);
  }, [syncEngine, waveId, presenceRetryKey, presencePollingOnly]);

  if (!endpointReady) {
    return <div className="empty-state">Configurazione cloud in corso…</div>;
  }

  return (
    <>
      <WaveShareDialog
        open={shareOpen}
        wave={wave}
        inviteEmail={inviteEmail}
        inviteError={inviteError}
        inviteSuccess={inviteSuccess}
        linkCopied={linkCopied}
        onClose={() => setShareOpen(false)}
        onInviteEmailChange={(email) => {
          setInviteEmail(email);
          setInviteError(null);
          setInviteSuccess(null);
        }}
        onInvite={() => {
          void (async () => {
            setInviteError(null);
            setInviteSuccess(null);
            try {
              const invited = await WaveCloudAPI.inviteParticipant(
                createWaveApiConfig(),
                waveId,
                inviteEmail,
                WaveRole.Editor,
              );
              setInviteEmail("");
              setInviteSuccess(`Invito inviato a ${invited.email ?? inviteEmail}`);
              void notifyWaveInvitePush(createWaveApiConfig(), {
                waveId,
                waveTitle: wave.title,
                recipientSub: invited.sub,
                inviterLabel: currentUserEmail() ?? "Un partecipante",
                spaBase: getPublicBaseUrl(),
              });
              onWaveReplace(
                await WaveCloudAPI.getWave(createWaveApiConfig(), waveId),
              );
              onInviteSuccess?.();
            } catch (e) {
              setInviteError(e instanceof Error ? e.message : "Invito non riuscito");
            }
          })();
        }}
        onCopyLink={() => {
          const url = `${getPublicBaseUrl()}/w/${waveId}`;
          void navigator.clipboard.writeText(url).then(() => {
            setLinkCopied(true);
            trackActivity("Condivisione", "Link wave copiato");
            window.setTimeout(() => setLinkCopied(false), 2500);
          });
        }}
      />
      <div className="wave-workspace">
      <div className="wave-editor-shell">
        {showSyncBar && <WaveSyncProgressBar />}
        {!showPlaceholder && (
          <WaveSyncStatusStrip
            presenceError={presenceLastError}
            networkHydrating={networkHydrating}
          />
        )}
        {!showPlaceholder && showSyncDebugBar && (
          <WaveSyncDiagnosticsBar
            diagnostics={syncDiagnostics}
            waveTitle={wave.title}
            presence={presenceDiagnostics}
          />
        )}
        {showPlaceholder ? (
          <WaveEditorPlaceholder />
        ) : (
          <WaveEditor
            waveId={waveId}
            wave={wave}
            bridge={bridge}
            editor={editor}
            documentRevision={documentRevision}
            legacyBlips={legacyBlips}
            presences={presences}
            lastSeen={lastSeen}
            playbackBaseline={baseline}
            playbackOps={ops}
            onEmitOps={emitOps}
            onFlushDocumentSync={flushDocumentSync}
            onPostComment={
              editor
                ? (anchor, body, parentId, galleryItemId) => {
                    const posted = editor.postComment(
                      anchor,
                      body,
                      parentId,
                      userSub,
                      resolveAuthorName(),
                      galleryItemId ?? null,
                    );
                    const { mentionsAssistant } = parseCommentMentions(body, mentionRoster);
                    void notifyWaveCommentMentionPush(createWaveApiConfig(), {
                      waveId,
                      waveTitle: wave.title,
                      body,
                      authorLabel: resolveAuthorName(),
                      participants: mentionRoster,
                      currentUserSub: userSub,
                      spaBase: getPublicBaseUrl(),
                    });
                    if (mentionsAssistant) {
                      void maybeRunCommentAgentReply({
                        editor,
                        waveId,
                        blockAnchorId: anchor,
                        postedComment: posted,
                        body,
                        allComments: editor.comments,
                      }).then(() => flushDocumentSync());
                    }
                  }
                : undefined
            }
            onToggleReaction={
              editor
                ? (commentId, emoji) => {
                    editor.toggleReaction(commentId, emoji, currentUserSub());
                  }
                : undefined
            }
            onPostLegacyBlip={
              !usesYjs
                ? async (anchor, body) => {
                    const b = await WaveCloudAPI.postBlip(
                      createWaveApiConfig(),
                      waveId,
                      anchor,
                      body,
                    );
                    setLegacyBlips((p) => [...p, b]);
                  }
                : undefined
            }
            onPlaybackBegin={() => setPlaybackEnabled(true)}
            onStatusChange={async (status: WaveStatus) => {
              onWaveReplace(
                await WaveCloudAPI.updateStatus(createWaveApiConfig(), waveId, status),
              );
            }}
            onArchiveToggle={onArchiveToggle}
            onRenameTitle={onRenameTitle}
            onPublishPresence={publishPresence}
            onRefreshPlayback={refreshPlayback}
            onCoverKeyChange={(id, keys) => {
              if (id === wave.waveId) {
                onWaveReplace({
                  ...wave,
                  ...(keys.coverImageS3Key?.trim()
                    ? { coverImageS3Key: keys.coverImageS3Key.trim() }
                    : {}),
                  ...(keys.coverImageHeaderS3Key?.trim()
                    ? { coverImageHeaderS3Key: keys.coverImageHeaderS3Key.trim() }
                    : {}),
                });
              }
            }}
            isFavorite={isFavorite}
            onToggleFavorite={onToggleFavorite}
            onInvite={() => setShareOpen(true)}
            onBack={onBack}
            backLabel={backLabel}
            sidebarCollapsed={sidebarCollapsed}
            onReopenSidebar={onReopenSidebar}
          />
        )}
        <WaveChatShell
          waveId={waveId}
          spaceId={wave.spaceId}
          editor={editor}
          launchedBy={userSub}
          chat={chat}
          open={chatOpen}
          onOpenChange={setChatOpen}
          onShowParticipants={() => setShareOpen(true)}
          onMissionStarted={() => flushDocumentSync()}
        />
      </div>
      </div>
    </>
  );
}
